import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import I18n from "i18n-js";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { actions } from "app/configSlice";
import { DialogType, RedirectSignUpSource } from "app/types";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { CloseBtn, PlanBadge, FadeTransition } from "common/elements";
import { getUserPlan } from "features/user/types";
import { ParentGate } from "./ParentGate";

export const RedirectSignUp = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleDialog = useDialog();
  const [titleKey, setTitleKey] = useState("");
  const [messageKey, setMessageKey] = useState("");
  const [parentGate, setParentGate] = useState(false);
  const redirect = useSelector(
    (state: RootState) => state.config.dialog.redirect_sign_up,
  );
  const parent = useSelector((state: RootState) => state.user.parent);

  useEffect(() => {
    if (redirect.value) {
      const source =
        (redirect.args?.source as RedirectSignUpSource) ??
        RedirectSignUpSource.COURSE;

      switch (source) {
        case RedirectSignUpSource.CREATOR:
          setTitleKey("MSG_REDIRECT_SIGNUP_TITLE_CREATOR");
          setMessageKey("MSG_REDIRECT_SIGNUP_MESSAGE_CREATOR");
          break;
        case RedirectSignUpSource.CHILD_USER_ACCOUNT_CREATION:
          setTitleKey("MSG_REDIRECT_SIGNUP_TITLE_CREATE_USER");
          setMessageKey("MSG_REDIRECT_SIGNUP_MESSAGE_CREATE_USER");
          break;
        case RedirectSignUpSource.PARENT_SETTINGS_USER_ACCOUNT_SETTINGS:
          setTitleKey(
            "MSG_REDIRECT_SIGNUP_TITLE_PARENT_SETTINGS_USER_ACCOUNT_SETTINGS",
          );
          setMessageKey(
            "MSG_REDIRECT_SIGNUP_MESSAGE_PARENT_SETTINGS_USER_ACCOUNT_SETTINGS",
          );
          break;
        case RedirectSignUpSource.PARENT_SETTINGS_CHILD_USER_SETTINGS:
          setTitleKey(
            "MSG_REDIRECT_SIGNUP_TITLE_PARENT_SETTINGS_CHILD_USER_SETTINGS",
          );
          setMessageKey(
            "MSG_REDIRECT_SIGNUP_MESSAGE_PARENT_SETTINGS_CHILD_USER_SETTINGS",
          );
          break;
        case RedirectSignUpSource.PARENT_SETTINGS_LINK_PARENTS_APP:
          setTitleKey(
            "MSG_REDIRECT_SIGNUP_TITLE_PARENT_SETTINGS_LINK_PARENTS_APP",
          );
          setMessageKey(
            "MSG_REDIRECT_SIGNUP_MESSAGE_PARENT_SETTINGS_LINK_PARENTS_APP",
          );
          break;
        case RedirectSignUpSource.PARENT_SETTINGS_ENABLE_LAB:
          setTitleKey("MSG_REDIRECT_SIGNUP_TITLE_PARENT_SETTINGS_ENABLE_LAB");
          setMessageKey(
            "MSG_REDIRECT_SIGNUP_MESSAGE_PARENT_SETTINGS_ENABLE_LAB",
          );
          break;
        case RedirectSignUpSource.COURSE:
        default:
          setTitleKey("MSG_REDIRECT_SIGNUP_TITLE_COURSE");
          setMessageKey("MSG_REDIRECT_SIGNUP_MESSAGE_COURSE");
          break;
      }
    }
  }, [redirect]);

  const handleClose = () => {
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.REDIRECT_SIGN_UP, value: false });
    });
  };

  const handleOpenBenefits = () => {
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.ACCOUNT_PROMOTION, value: true });
    });
  };

  const handleParentGateClose = () => setParentGate(false);
  const handleParentGateConfirm = () => {
    setParentGate(false);
    dispatch(actions.clearDialog());
    navigate("/auth/register");
  };

  return (
    <>
      <Dialog
        open={redirect.value}
        maxWidth={false}
        sx={{ zIndex: 99999999 }}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div
          className="flex-col-el flex-center relative"
          style={{ transform: `scale(${scale})` }}
        >
          <div
            className="flex-col-el flex-center relative"
            style={{
              filter:
                "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
            }}
          >
            <div className="flex-col-el justify-start w-[650px] relative border-solid border-white border-[8px] rounded-[8px] bg-white">
              <CloseBtn close={handleClose} />

              <div className="flex-col-view justify-start w-full h-full relative p-6 rounded-[4px] bg-beige-60">
                <div className="flex-row-view items-center justify-start pb-4">
                  <div className="flex-col-center mr-2">
                    <span className="material-symbols-outlined !text-4xl text-[#4C9DF1]">
                      info
                    </span>
                  </div>
                  <p className="text-body text-textcolor-black text-[26px]">
                    {I18n.t(titleKey)}
                  </p>
                </div>

                <div className="flex-col-view items-start py-4">
                  {I18n.t(messageKey)
                    .split("\n")
                    .map((line, index) => {
                      return (
                        <div
                          key={index}
                          className="flex-row-view flex-wrap mb-3"
                        >
                          <p className="text-body text-textcolor-black leading-8 text-xl">
                            ・
                          </p>
                          <p className="text-body text-textcolor-black leading-8 text-xl flex-1">
                            {line}
                          </p>
                        </div>
                      );
                    })}

                  <div className="w-full flex-row-el justify-between items-center">
                    <div className="flex-row-el flex-center gap-2">
                      <p className="text-body text-textcolor-black text-xl">
                        {I18n.t("MSG_CURRENT_PLAN")}:
                      </p>
                      <PlanBadge plan={getUserPlan(parent)} />
                    </div>
                  </div>
                </div>

                <div className="flex-row-el flex-center gap-6 mt-4 px-0 alpha">
                  <button className="btn btn-blue" onClick={handleClose}>
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span className="material-symbols-outlined !text-4xl text-white">
                        close
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_ACCOUNT_PROMOTION_LATER_BTN")}
                      </p>
                    </div>
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={handleOpenBenefits}
                  >
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span className="material-symbols-outlined !text-4xl text-white">
                        admin_panel_settings
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_REDIRECT_SIGNUP_TO_PARENTS_BTN")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate
        visible={parentGate}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />
    </>
  );
};
